/* ==========================================================================
   Slideshow: Split — transform-track seamless infinite loop.

   Instead of native scroll-snap (whose loop reset depends on an imprecise
   settle timer and can lag / jump when the loop closes), this moves an explicit
   translateX track and resets on the `transitionend` event — the exact frame
   the slide animation ends — so the wrap is invisible. Technique adapted from
   the V4 Media Cards slider.

   Layout: [lead clones][real slides][trailing clones]. `index` is the leftmost
   visible slide. Advancing into the clones and then silently snapping back by
   `realCount` (with transitions off) reads as one continuous loop. Two clones
   per side cover the desktop 2-up so no blank ever shows during the wrap.

   Slides are sized in JS (slider width / slides-in-view) so widths are
   independent of the flex container. Arrows + touch-swipe drive navigation;
   autoplay is viewport-gated and pauses on hover / focus.
   ========================================================================== */

(function () {
  var DURATION = 500; // ms — slide transition
  var registry = {};  // by section id, so editor re-init / re-render cleans up

  function slidesInView() {
    return window.innerWidth < 750 ? 1 : 2;
  }

  function initInstance(root) {
    if (!root) return;
    
    var id = root.id || '';

    // Clean up any previous instance for this section (theme editor re-render).
    if (id && registry[id]) {
      registry[id]();
      delete registry[id];
    }
    if (root.dataset.slsInit === 'true') return;

    var slider = root.querySelector('[id^="Slider-"]');
    if (!slider) return;

    var reals = Array.prototype.slice.call(
      slider.querySelectorAll('.slideshow__slide[id^="Slide-"]')
    );
    var realCount = reals.length;
    if (realCount === 0) return;
    root.dataset.slsInit = 'true';

    var loopable = realCount > 1;
    var clones = loopable ? Math.min(2, realCount) : 0; // per side; 2 covers the 2-up
    var index = clones;   // start on the first real slide
    var step = 0;         // px width of one slide
    var animating = false;

    // ---- build the track: [lead clones][reals][trailing clones] ----
    var track = document.createElement('div');
    track.className = 'slideshow-split__track';

    function makeClone(node) {
      var c = node.cloneNode(true);
      c.removeAttribute('id');
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('tabindex', '-1');
      c.classList.add('slideshow-split__clone');
      return c;
    }

    var i;
    if (loopable) {
      for (i = realCount - clones; i < realCount; i++) track.appendChild(makeClone(reals[i]));
    }
    reals.forEach(function (r) { track.appendChild(r); });
    if (loopable) {
      for (i = 0; i < clones; i++) track.appendChild(makeClone(reals[i]));
    }
    slider.appendChild(track);

    var slides = Array.prototype.slice.call(track.querySelectorAll('.slideshow__slide'));

    // ---- sizing (independent of the flex container) ----
    function measure() {
      step = slider.clientWidth / slidesInView();
      slides.forEach(function (s) {
        s.style.flex = '0 0 ' + step + 'px';
        s.style.width = step + 'px';
        s.style.maxWidth = 'none';
      });
    }

    function place(i, animate) {
      index = i;
      track.style.transition = animate ? 'transform ' + DURATION + 'ms ease' : 'none';
      track.style.transform = 'translateX(' + (-i * step) + 'px)';
    }

    measure();
    place(index, false);

    // ---- seamless reset the instant the slide animation ends ----
    if (loopable) {
      track.addEventListener('transitionend', function (e) {
        if (e.target !== track || e.propertyName !== 'transform') return;
        if (index >= clones + realCount) {
          place(index - realCount, false);   // stepped into trailing clones
        } else if (index < clones) {
          place(index + realCount, false);   // stepped into leading clones
        }
        animating = false;
      });
    }

    var goNext = function () {
      if (!loopable || animating) return;
      animating = true;
      place(index + 1, true);
    };
    var goPrev = function () {
      if (!loopable || animating) return;
      animating = true;
      place(index - 1, true);
    };

    // ---- arrows (capture, so the engine's scroll handler never runs) ----
    var bindArrow = function (name, fn) {
      var btn = root.querySelector('button.slider-arrow[name="' + name + '"]');
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.stopImmediatePropagation();
        e.preventDefault();
        fn();
      }, true);
    };
    bindArrow('next', goNext);
    bindArrow('previous', goPrev);

    // ---- touch swipe (mobile 1-up) ----
    var touchX = null;
    slider.addEventListener('touchstart', function (e) {
      touchX = e.changedTouches[0].screenX;
    }, { passive: true });
    slider.addEventListener('touchend', function (e) {
      if (touchX === null) return;
      var dx = e.changedTouches[0].screenX - touchX;
      touchX = null;
      if (Math.abs(dx) < 40) return;
      if (dx < 0) goNext(); else goPrev();
    }, { passive: true });

    // ---- re-measure on resize (full/compact + breakpoint changes) ----
    var resizeTimer = null;
    var onResize = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { measure(); place(index, false); }, 150);
    };
    window.addEventListener('resize', onResize, { passive: true });

    // ---- autoplay (viewport-gated, pauses on hover / focus) ----
    var haltAutoplay = function () {};
    var observer = null;
    if (loopable && slider.getAttribute('data-slp-autoplay') === 'true') {
      var speed = parseFloat(slider.getAttribute('data-speed')) || 5;
      var timer = null, visible = false, hovered = false;
      haltAutoplay = function () { if (timer) clearInterval(timer); timer = null; };
      var refresh = function () {
        haltAutoplay();
        if (visible && !hovered) timer = setInterval(goNext, speed * 1000);
      };
      root.addEventListener('mouseenter', function () { hovered = true; refresh(); });
      root.addEventListener('mouseleave', function () { hovered = false; refresh(); });
      root.addEventListener('focusin', function () { hovered = true; refresh(); });
      root.addEventListener('focusout', function () { hovered = false; refresh(); });
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          refresh();
        }, { threshold: 0.2 });
        observer.observe(root);
      } else {
        visible = true;
        refresh();
      }
    }

    // ---- teardown (used on theme editor re-render) ----
    if (id) {
      registry[id] = function () {
        haltAutoplay();
        if (observer) observer.disconnect();
        window.removeEventListener('resize', onResize);
      };
    }
  }

  function initAll() {
    document.querySelectorAll('.slideshow-split-section').forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: Shopify replaces the section markup, so re-init on the fresh
  // node (registry cleanup stops the previous instance's timers/observer).
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target.querySelector('.slideshow-split-section');
      if (root) initInstance(root);
    });
  }
})();
